import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, MapPin, MagnifyingGlassPlus, MagnifyingGlassMinus, ArrowsOutCardinal, Wine, Leaf, ForkKnife, Globe } from '@phosphor-icons/react';
import InteractiveWineMap from '../Maps/InteractiveWineMap';
import { Breadcrumb } from '../Product/Breadcrumb';

// Import country data functions
import { getFranceData } from '../../data/countries/france';
import { getSpainData } from '../../data/countries/spain';
import { getPortugalData } from '../../data/countries/portugal';
import { getItalyData } from '../../data/countries/italy';
import { getArgentinaData } from '../../data/countries/argentina';
import { getChileData } from '../../data/countries/chile';
import { getAustraliaData } from '../../data/countries/australia';
import { getAustriaData } from '../../data/countries/austria';
import { getSwitzerlandData } from '../../data/countries/switzerland';
import { getMexicoData } from '../../data/countries/mexico';
import { getGermanyData } from '../../data/countries/germany';
import { getGreeceData } from '../../data/countries/greece';
import { getNewZealandData } from '../../data/countries/new-zealand';

// Data mapping object - using functions
const countryDataMap = {
  'france': getFranceData,
  'spain': getSpainData,
  'portugal': getPortugalData,
  'italy': getItalyData,
  'argentina': getArgentinaData,
  'chile': getChileData,
  'australia': getAustraliaData,
  'austria': getAustriaData,
  'switzerland': getSwitzerlandData,
  'mexico': getMexicoData,
  'germany': getGermanyData,
  'greece': getGreeceData,
  'new-zealand': getNewZealandData,
};

export const CountryInfoPage = () => {
  const { country } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    
    const key = country;
    console.log('Debug - country:', country);
    console.log('Debug - key:', key);
    console.log('Debug - available keys:', Object.keys(countryDataMap));
    console.log('Debug - countryDataMap[key]:', countryDataMap[key]);
    
    const getCountryData = countryDataMap[key];
    
    if (getCountryData) {
      setData(getCountryData());
      setLoading(false);
    } else {
      setError('no-info');
      setLoading(false);
    }
  }, [country, navigate]);

  if (loading) {
    return <main style={{ padding: 40, textAlign: 'center' }}>Chargement...</main>;
  }
  
  if (error === 'no-info') {
    return (
      <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Breadcrumb />
        
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
            onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
            onMouseOut={e => e.currentTarget.style.background = 'none'}
          >
            <ArrowLeft size={24} color="#4B164C" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>{country}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
              <Globe size={16} />
              <span>Pays viticole</span>
            </div>
          </div>
        </div>

        {/* No Info Available Message */}
        <section style={{ 
          background: '#f8f6fa', 
          borderRadius: '16px', 
          padding: '48px 32px', 
          boxShadow: '0 4px 24px rgba(75,22,76,0.08)',
          textAlign: 'center'
        }}>
          <div style={{ 
            width: '80px', 
            height: '80px', 
            background: '#4B164C', 
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <Globe size={32} color="#fff" />
          </div>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>
            Informations non disponibles
          </h2>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#666', margin: '0 0 32px 0', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
            Les informations pour le pays <strong>{country}</strong> ne sont pas encore disponibles dans notre base de données. 
            Nous travaillons continuellement à enrichir notre guide du vin.
          </p>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'center' }}>
            <button 
              onClick={() => navigate('/info/countries')}
              style={{
                background: '#4B164C',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
              onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
            >
              Explorer d'autres pays
            </button>
            <button 
              onClick={() => navigate('/info')}
              style={{
                background: '#f0e6f0',
                color: '#4B164C',
                border: '1px solid #4B164C',
                padding: '12px 24px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '500',
                transition: 'background 0.2s'
              }}
              onMouseOver={e => e.currentTarget.style.background = '#e0d6e0'}
              onMouseOut={e => e.currentTarget.style.background = '#f0e6f0'}
            >
              Guide du Vin
            </button>
          </div>
        </section>
      </main>
    );
  }
  
  if (error) {
    return (
      <main style={{ padding: 40, textAlign: 'center' }}>
        <h1>Erreur</h1>
        <p>Une erreur s'est produite lors du chargement des données.</p>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: '#4B164C',
            color: '#fff',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px'
          }}
        >Retour</button>
      </main>
    );
  }
  if (!data) return null;

  return (
    <main style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <Breadcrumb />
      
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '32px', paddingBottom: '16px', borderBottom: '1px solid #e8e0e8' }}>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}
          onMouseOver={e => e.currentTarget.style.background = '#f0e6f0'}
          onMouseOut={e => e.currentTarget.style.background = 'none'}
        >
          <ArrowLeft size={24} color="#4B164C" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '32px', color: '#4B164C', margin: '0 0 4px 0', fontWeight: '700' }}>{data.name}</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#666', fontSize: '16px' }}>
            <Globe size={16} />
            <span>Pays viticole</span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/search?pays_origine=${encodeURIComponent(data.name)}`)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 20px',
            background: '#4B164C',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: '500',
            transition: 'background 0.2s'
          }}
          onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
          onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
        >
          <MagnifyingGlassPlus size={18} />
          Rechercher des vins
        </button>
      </div>

      {/* Main Content */}
      <div style={{ display: 'grid', gap: '32px' }}>
        {/* Description */}
        <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>Description</h2>
          <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#333', margin: '0' }}>{data.description}</p>
        </section>

        {/* Interactive Map */}
        {data.mapAreas && (
          <InteractiveWineMap mapAreas={data.mapAreas} country={data.name} />
        )}

        {/* Statistics */}
        {data.statistics && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Statistiques</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '24px' }}>
              {Object.entries(data.statistics).map(([key, value]) => (
                <div key={key} style={{ background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e8e0e8', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', color: '#4B164C', fontWeight: '700', marginBottom: '8px' }}>{value}</div>
                  <div style={{ fontSize: '14px', color: '#666', textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1').trim()}</div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Regional Breakdown */}
        {data.regions && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0', fontWeight: '600' }}>Régions Viticoles</h2>
              <button
                onClick={() => navigate(`/info/countries/${country}/regions`)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 16px',
                  background: '#4B164C',
                  color: 'white',
                  border: 'none',
                  borderRadius: '20px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#6d2c7a'}
                onMouseOut={e => e.currentTarget.style.background = '#4B164C'}
              >
                Voir toutes les régions ({data.regions.length})
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
              {data.regions.map((region, idx) => (
                <div key={idx} style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e8e0e8', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ fontSize: '16px', color: '#4B164C', margin: '0', fontWeight: '600' }}>{region.name}</h3>
                    <span style={{ background: data.mapAreas && data.mapAreas[idx]?.color || '#4B164C', color: '#fff', padding: '4px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: '600' }}>{region.percentage}</span>
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>{region.description}</div>
                  <div style={{ color: '#aaa', fontSize: '12px' }}>{region.area}</div>
                  <button
                    onClick={() => navigate(`/info/countries/${country}/regions/${region.slug || region.name.toLowerCase().replace(/\s+/g, '-')}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                      padding: '8px 12px',
                      background: '#f0e6f0',
                      color: '#4B164C',
                      border: '1px solid #4B164C',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      transition: 'all 0.2s',
                      marginTop: '8px'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.background = '#4B164C';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.background = '#f0e6f0';
                      e.currentTarget.style.color = '#4B164C';
                    }}
                  >
                    <MapPin size={14} />
                    Explorer cette région
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Characteristics */}
        {data.characteristics && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Caractéristiques</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
              {Object.entries(data.characteristics).map(([key, value]) => (
                <div key={key}>
                  <h3 style={{ fontSize: '18px', color: '#4B164C', margin: '0 0 12px 0', fontWeight: '600', textTransform: 'capitalize' }}>{key}</h3>
                  <p style={{ fontSize: '16px', color: '#333', margin: '0', lineHeight: '1.5' }}>{value}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Wine Styles */}
        {data.wineStyles && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600' }}>Styles de Vins</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {Object.entries(data.wineStyles).map(([style, description], index) => (
                <div key={index} style={{ background: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #e8e0e8', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <div style={{ background: '#4B164C', color: '#fff', width: '24px', height: '24px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600', flexShrink: 0, marginTop: '2px' }}>{index + 1}</div>
                  <div>
                    <h3 style={{ fontSize: '16px', color: '#4B164C', margin: '0 0 8px 0', fontWeight: '600', textTransform: 'capitalize' }}>{style}</h3>
                    <p style={{ fontSize: '14px', color: '#333', margin: '0', lineHeight: '1.5' }}>{description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* History */}
        {data.history && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600' }}>Histoire</h2>
            <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#333', margin: '0' }}>{data.history}</p>
          </section>
        )}

        {/* Food Pairing */}
        {data.foodPairing && (
          <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
            <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ForkKnife size={24} />
              Accords Mets & Vins
            </h2>
            <p style={{ fontSize: '18px', lineHeight: '1.6', color: '#333', margin: '0' }}>{data.foodPairing}</p>
          </section>
        )}

        {/* Continue Exploring Navigation */}
        <section style={{ background: '#f8f6fa', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 24px rgba(75,22,76,0.08)' }}>
          <h2 style={{ fontSize: '24px', color: '#4B164C', margin: '0 0 24px 0', fontWeight: '600', textAlign: 'center' }}>
            Continuez à Explorer
          </h2>
          
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Other Countries Section */}
            <div>
              <h3 style={{ fontSize: '20px', color: '#4B164C', margin: '0 0 16px 0', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Globe size={20} />
                Autres Pays Viticoles
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                {Object.keys(countryDataMap).filter(c => c !== country).map((otherCountry, idx) => {
                  const getOtherCountryData = countryDataMap[otherCountry];
                  if (!getOtherCountryData) return null;
                  
                  const otherCountryData = getOtherCountryData();
                  return (
                    <button
                      key={idx}
                      onClick={() => navigate(`/info/countries/${otherCountry}`)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        background: '#fff',
                        border: '2px solid #e8e0e8',
                        borderRadius: '12px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        textAlign: 'left',
                        width: '100%'
                      }}
                      onMouseOver={e => {
                        e.currentTarget.style.borderColor = '#4B164C';
                        e.currentTarget.style.background = '#f0e6f0';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseOut={e => {
                        e.currentTarget.style.borderColor = '#e8e0e8';
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div style={{ 
                        width: '40px', 
                        height: '40px', 
                        background: '#4B164C', 
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 'bold'
                      }}>
                        {otherCountryData.name.charAt(0)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: '#4B164C', marginBottom: '4px' }}>
                          {otherCountryData.name}
                        </div>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                          {otherCountryData.regions?.length || 0} régions
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Quick Actions */}
            <div style={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: '12px', 
              justifyContent: 'center',
              marginTop: '16px'
            }}>
              <button
                onClick={() => navigate('/info/countries')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#6d2c7a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'background 0.2s'
                }}
                onMouseOver={e => e.currentTarget.style.background = '#8a4a8a'}
                onMouseOut={e => e.currentTarget.style.background = '#6d2c7a'}
              >
                <Globe size={18} />
                Tous les Pays
              </button>
              <button
                onClick={() => navigate('/info')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 20px',
                  background: '#f0e6f0',
                  color: '#4B164C',
                  border: '2px solid #4B164C',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={e => {
                  e.currentTarget.style.background = '#4B164C';
                  e.currentTarget.style.color = 'white';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.background = '#f0e6f0';
                  e.currentTarget.style.color = '#4B164C';
                }}
              >
                <MapPin size={18} />
                Guide du Vin
              </button>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}; 